/**
 * Hand-written TS model for the `bookingFields` schema delivered on the shop
 * response (CT1265T-135). The generated `@b2bportal/ground-api` client types
 * `bookingFields` only as `Array<any>`, and `redmond`'s `CarShopResponse` omits
 * the field entirely — so we model it here and read `extraInfo.bookingFields`
 * untyped at the call site.
 *
 * Wire shape: Hopper's sealed traits serialize with an INTERNAL discriminator
 * keyed by the trait name, with the case-class fields flattened as siblings —
 * e.g. `{ "BookingField": "Compound", "id": "flight-info", ... }` and
 * `{ "BookingFieldValidation": "Pattern", "regex": "..." }`. (Confirmed against
 * Helpers GroundSpec.scala round-trips, b2b-interfaces BookingField.scala, and
 * the ground.yml discriminator mappings.) The parser is deliberately defensive
 * so an unexpected payload degrades to "no flight section" rather than throwing.
 */

export type BookingFieldRequirement = "Required" | "Optional";

export type BookingFieldType = "String" | "Number" | "Boolean" | "DateTime";

// Validations are a sealed trait discriminated by the internal
// `BookingFieldValidation` tag; the constraint value is a flattened sibling.
export interface PatternValidation {
  BookingFieldValidation: "Pattern";
  regex: string;
}
export interface MinLengthValidation {
  BookingFieldValidation: "MinLength";
  value: number;
}
export interface MaxLengthValidation {
  BookingFieldValidation: "MaxLength";
  value: number;
}
export interface MinValidation {
  BookingFieldValidation: "Min";
  value: number;
}
export interface MaxValidation {
  BookingFieldValidation: "Max";
  value: number;
}
export type BookingFieldValidation =
  | PatternValidation
  | MinLengthValidation
  | MaxLengthValidation
  | MinValidation
  | MaxValidation;

export interface PrimitiveBookingField {
  BookingField: "Primitive";
  id: string;
  requirement: BookingFieldRequirement;
  fieldType: BookingFieldType;
  validations: BookingFieldValidation[];
}

export interface CompoundBookingField {
  BookingField: "Compound";
  id: string;
  requirement: BookingFieldRequirement;
  fields: BookingField[];
}

// Sealed trait discriminated by the internal `BookingField` tag.
export type BookingField = PrimitiveBookingField | CompoundBookingField;

// --- Sentinels -------------------------------------------------------------

export const FLIGHT_INFO_FIELD_ID = "flight-info";
export const AIRLINE_CODE_FIELD_ID = "airline-code";
export const FLIGHT_NUMBER_FIELD_ID = "flight-number";

// --- Normalized view the checkout UI consumes ------------------------------

export interface PrimitiveFieldSpec {
  id: string;
  required: boolean;
  /** Compiled from the field's `pattern` validation, if any. */
  pattern?: RegExp;
}

export interface FlightInfoFieldSpec {
  /** compound.requirement === "Optional" ⇒ both-empty allowed. */
  optional: boolean;
  airlineCode: PrimitiveFieldSpec;
  flightNumber: PrimitiveFieldSpec;
}

/**
 * The flight-info values a traveler enters at checkout (as opposed to
 * {@link FlightInfoFieldSpec}, which describes the field the provider
 * requested). Stored per-booking in the book reducer; never persisted to the
 * traveler profile.
 */
export interface FlightInfoValues {
  /** 2-letter IATA airline code from the dropdown, e.g. "AA". */
  airlineCode?: string;
  /** Flight number, e.g. "1234". */
  flightNumber?: string;
}

// --- Type guards -----------------------------------------------------------

const isCompound = (
  field: BookingField
): field is CompoundBookingField =>
  !!field && typeof field === "object" && field.BookingField === "Compound";

const isPrimitive = (
  field: BookingField
): field is PrimitiveBookingField =>
  !!field && typeof field === "object" && field.BookingField === "Primitive";

const compilePattern = (
  validations: BookingFieldValidation[] | undefined
): RegExp | undefined => {
  const patternValidation = (validations ?? []).find(
    (v): v is PatternValidation =>
      !!v && typeof v === "object" && v.BookingFieldValidation === "Pattern"
  );
  // Bound the source before compiling: provider schema patterns are trivially short, so reject oversized ones to limit ReDoS surface.
  if (!patternValidation?.regex || patternValidation.regex.length > 100) {
    return undefined;
  }
  try {
    return new RegExp(patternValidation.regex);
  } catch {
    return undefined;
  }
};

const toPrimitiveSpec = (
  primitive: PrimitiveBookingField
): PrimitiveFieldSpec => ({
  id: primitive.id,
  required: primitive.requirement === "Required",
  pattern: compilePattern(primitive.validations),
});

/**
 * Extract the `flight-info` compound from the shop response's `bookingFields`.
 * Returns a normalized {@link FlightInfoFieldSpec} when the provider advertises
 * the flight-info field (with both airline-code and flight-number inner
 * fields), otherwise `null`. Defensive against unexpected shapes / partial
 * data so an unknown provider payload never breaks checkout render.
 */
export const parseFlightInfoField = (
  bookingFields: unknown
): FlightInfoFieldSpec | null => {
  if (!Array.isArray(bookingFields)) {
    return null;
  }

  const flightInfo = (bookingFields as BookingField[])
    .filter(isCompound)
    .find((c) => c?.id === FLIGHT_INFO_FIELD_ID);

  if (!flightInfo || !Array.isArray(flightInfo.fields)) {
    return null;
  }

  const primitives = flightInfo.fields.filter(isPrimitive);

  const airline = primitives.find((p) => p?.id === AIRLINE_CODE_FIELD_ID);
  const flightNumber = primitives.find(
    (p) => p?.id === FLIGHT_NUMBER_FIELD_ID
  );

  // Require both inner fields the figma renders; arrival-date-time is ignored.
  if (!airline || !flightNumber) {
    return null;
  }

  return {
    optional: flightInfo.requirement !== "Required",
    airlineCode: toPrimitiveSpec(airline),
    flightNumber: toPrimitiveSpec(flightNumber),
  };
};